import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "./UserContext";
import { checkRoleAccess } from "../../utils/roleHelper";
import {
  FaBookOpen,
  FaBullhorn,
  FaCalendarAlt,
  FaCheckCircle,
  FaEnvelope,
  FaFileInvoiceDollar,
  FaGavel,
  FaMapMarkedAlt,
  FaRunning,
  FaTrophy,
  FaUniversity,
  FaUserCircle,
  FaUsers,
  FaVideo,
  FaVoteYea,
} from "react-icons/fa";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const SHORTCUTS = [
  {
    label: "Calendrier",
    desc: "Ligue & Comités",
    to: "/calendrier-ligue",
    icon: FaCalendarAlt,
    roleRequired: [],
    priority: 1,
    theme: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      iconBg: "bg-blue-500",
      iconText: "text-white",
      hover: "hover:border-blue-300",
    },
  },
  {
    label: "Techniques",
    desc: "Gokyo, kata…",
    to: "/techniques-judo",
    icon: FaBookOpen,
    roleRequired: [],
    priority: 2,
    theme: {
      bg: "bg-orange-50",
      border: "border-orange-200",
      iconBg: "bg-orange-500",
      iconText: "text-white",
      hover: "hover:border-orange-300",
    },
  },
  {
    label: "Clubs NA",
    desc: "Trouver un club",
    to: "/clubs-na",
    icon: FaUsers,
    roleRequired: [],
    priority: 3,
    theme: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      iconBg: "bg-emerald-500",
      iconText: "text-white",
      hover: "hover:border-emerald-300",
    },
  },
  {
    label: "Annonces",
    desc: "Postes …",
    to: "/petites-annonces",
    icon: FaBullhorn,
    roleRequired: [],
    priority: 4,
    theme: {
      bg: "bg-pink-50",
      border: "border-pink-200",
      iconBg: "bg-pink-500",
      iconText: "text-white",
      hover: "hover:border-pink-300",
    },
  },
  {
    label: "Inscription CN",
    desc: "Centre National",
    to: "/inscriptions-events",
    icon: FaCheckCircle,
    roleRequired: [],
    priority: 5,
    theme: {
      bg: "bg-violet-50",
      border: "border-violet-200",
      iconBg: "bg-violet-500",
      iconText: "text-white",
      hover: "hover:border-violet-300",
    },
  },
  {
    label: "Arbitrage",
    desc: "Règles & vidéos",
    to: "/reglement-arbitrage",
    icon: FaGavel,
    roleRequired: [],
    priority: 6,
    theme: {
      bg: "bg-cyan-50",
      border: "border-cyan-200",
      iconBg: "bg-cyan-500",
      iconText: "text-white",
      hover: "hover:border-cyan-300",
    },
  },
  {
    label: "Résultats",
    desc: "Compétitions",
    to: "/resultats-calendrier-ligue",
    icon: FaTrophy,
    roleRequired: [],
    priority: 7,
    theme: {
      bg: "bg-rose-50",
      border: "border-rose-200",
      iconBg: "bg-rose-500",
      iconText: "text-white",
      hover: "hover:border-rose-300",
    },
  },
  {
    label: "Live Events",
    desc: "Répartitions",
    to: "/live-events",
    icon: FaVideo,
    roleRequired: [],
    priority: 8,
    theme: {
      bg: "bg-indigo-50",
      border: "border-indigo-200",
      iconBg: "bg-indigo-500",
      iconText: "text-white",
      hover: "hover:border-indigo-300",
    },
  },
  {
    label: "Commissions",
    desc: "La Ligue",
    to: "/commissions-ligue",
    icon: FaUniversity,
    roleRequired: [],
    priority: 9,
    theme: {
      bg: "bg-teal-50",
      border: "border-teal-200",
      iconBg: "bg-teal-500",
      iconText: "text-white",
      hover: "hover:border-teal-300",
    },
  },
  {
    label: "Catégories",
    desc: "Âge & poids",
    to: "/categories",
    icon: FaRunning,
    roleRequired: [],
    priority: 10,
    theme: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      iconBg: "bg-amber-500",
      iconText: "text-white",
      hover: "hover:border-amber-300",
    },
  },
  {
    label: "Infos CN",
    desc: "Centre National",
    to: "/infocn",
    icon: FaMapMarkedAlt,
    roleRequired: [],
    priority: 11,
    theme: {
      bg: "bg-fuchsia-50",
      border: "border-fuchsia-200",
      iconBg: "bg-fuchsia-500",
      iconText: "text-white",
      hover: "hover:border-fuchsia-300",
    },
  },

  // Connecté uniquement
  {
    label: "Profil",
    desc: "Badges & compte",
    to: "/profile-utilisateur",
    icon: FaUserCircle,
    roleRequired: [1, 2, 3, 4, 5, 6, 7, 8],
    authOnly: true,
    priority: 12,
    theme: {
      bg: "bg-slate-50",
      border: "border-slate-200",
      iconBg: "bg-slate-700",
      iconText: "text-white",
      hover: "hover:border-slate-300",
    },
  },
  {
    label: "Messages",
    desc: "Messagerie",
    to: "/messagerie",
    icon: FaEnvelope,
    roleRequired: [1, 3, 4, 5, 6, 7, 8],
    authOnly: true,
    priority: 13,
    theme: {
      bg: "bg-sky-50",
      border: "border-sky-200",
      iconBg: "bg-sky-500",
      iconText: "text-white",
      hover: "hover:border-sky-300",
    },
  },
  {
    label: "Votes",
    desc: "Électroniques",
    to: "/votes-utilisateur",
    icon: FaVoteYea,
    roleRequired: [1, 2, 3, 4, 5, 6, 7, 8],
    authOnly: true,
    priority: 14,
    theme: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      iconBg: "bg-blue-700",
      iconText: "text-white",
      hover: "hover:border-blue-300",
    },
  },
  {
    label: "Convocations",
    desc: "Mes convocations",
    to: "/mesconvocations",
    icon: FaEnvelope,
    roleRequired: [1, 4, 5, 6, 7],
    authOnly: true,
    priority: 15,
    theme: {
      bg: "bg-lime-50",
      border: "border-lime-200",
      iconBg: "bg-lime-600",
      iconText: "text-white",
      hover: "hover:border-lime-300",
    },
  },
  {
    label: "Fiche de frais",
    desc: "Mes remboursements",
    to: "/fiche-de-frais",
    icon: FaFileInvoiceDollar,
    roleRequired: [1, 4, 5, 6, 7],
    authOnly: true,
    priority: 16,
    theme: {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      iconBg: "bg-yellow-500",
      iconText: "text-white",
      hover: "hover:border-yellow-300",
    },
  },
];

function HomeQuickShortcuts() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const visibleShortcuts = useMemo(() => {
    return SHORTCUTS.filter((item) => {
      if (item.authOnly && !user) return false;
      if (!item.roleRequired || item.roleRequired.length === 0) return true;
      if (!user?.role_id) return false;
      return checkRoleAccess(user.role_id, item.roleRequired);
    }).sort((a, b) => a.priority - b.priority);
  }, [user]);

  const initialCount = 8;
  const displayedShortcuts = expanded
    ? visibleShortcuts
    : visibleShortcuts.slice(0, initialCount);

  const hasMore = visibleShortcuts.length > initialCount;

  return (
    <section className="space-y-5">
      <div className="text-center">
        <h2 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
          Raccourcis rapides
        </h2>
        <p className="mt-2 text-sm text-slate-600 sm:text-base">
          Accédez en un clic aux sections clés de la Ligue
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {displayedShortcuts.map((item) => {
          const Icon = item.icon;

          return (
            <button
              key={item.to}
              type="button"
              onClick={() => navigate(item.to)}
              className={cx(
                "group rounded-2xl border p-3 text-center shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                item.theme.bg,
                item.theme.border,
                item.theme.hover
              )}
            >
              <div
                className={cx(
                  "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm sm:h-14 sm:w-14",
                  item.theme.iconBg,
                  item.theme.iconText
                )}
              >
                <Icon className="text-lg sm:text-xl" />
              </div>

              <h3 className="line-clamp-2 text-sm font-extrabold text-slate-900 sm:text-[15px]">
                {item.label}
              </h3>

              <p className="mt-1 line-clamp-2 text-xs text-slate-600 sm:text-sm">
                {item.desc}
              </p>
            </button>
          );
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            {expanded ? "Voir moins" : "Afficher plus"}
          </button>
        </div>
      )}
    </section>
  );
}

export default HomeQuickShortcuts;